/**
 * AVBP Portal — DURABLE admin-action queue (client transport + pure shape).
 *
 * ─────────────────────────────────────────────────────────────────────────
 *  WHY THIS EXISTS
 * ─────────────────────────────────────────────────────────────────────────
 *  Owner decision 2026-05-18: while the owner is away (Kailash Yatra,
 *  unreachable ~26 May → 08 Jun) the desktop confirm-link for HIGH-risk
 *  WhatsApp admin commands is useless. HIGH-risk commands now persist as a
 *  DURABLE server-side queue (admin_action_queue/), reviewed by the owner
 *  in ONE batch on return.
 *
 *  The queue collection is server-only (firestore.rules: read,write:false).
 *  The portal NEVER touches Firestore directly for it — it goes through two
 *  admin-role-guarded callables:
 *    - listAdminActionQueue : returns the queued items (+ recent decided)
 *    - decideAdminAction    : approve→execute | reject ONE item
 *  Both re-check the admin role + single-use + tier SERVER-SIDE; the client
 *  verdict is never trusted (conversation visibility model: this surface is
 *  team/admin-only — clients/staff never see it).
 *
 *  The pure helpers (statusLabel / isPending / sortForReview) are exported
 *  so the Node test runner exercises the display contract without React or
 *  Firebase.
 * ─────────────────────────────────────────────────────────────────────────
 */

export type QueueStatus =
  | 'queued'
  | 'approved'
  | 'rejected'
  | 'executed'
  | 'expired';

export interface QueueItem {
  id: string;
  status: QueueStatus;
  summary: string;
  tier: string;
  tierReason: string | null;
  from: string | null;
  staffId: string | null;
  waMessageId: string | null;
  action: {
    entity?: string;
    op?: string;
    fields?: Record<string, string | number | boolean>;
  } | null;
  receipt: string | null;
  rejectReason: string | null;
  createdAt: number | null;
  decidedAt: number | null;
}

export interface DecideResult {
  ok: boolean;
  status?: QueueStatus;
  receipt?: string | null;
  queueId?: string;
}

/* ════════════════════ pure display helpers ════════════════════ */

/** Human label for a queue status (review screen + tests). */
export function statusLabel(s: QueueStatus): string {
  switch (s) {
    case 'queued':
      return 'Awaiting your review';
    case 'approved':
      return 'Approved (running)';
    case 'rejected':
      return 'Rejected';
    case 'executed':
      return 'Done';
    case 'expired':
      return 'Expired';
    default:
      return s;
  }
}

/** Only a queued item is still decidable by the owner (single-use). */
export function isPending(s: QueueStatus): boolean {
  return s === 'queued';
}

/**
 * Order for the review screen: queued (actionable) first, then most
 * recently created. Pure + stable so the list never jumps around and the
 * test can assert ordering.
 */
export function sortForReview(items: QueueItem[]): QueueItem[] {
  return [...items].sort((a, b) => {
    const ap = isPending(a.status) ? 0 : 1;
    const bp = isPending(b.status) ? 0 : 1;
    if (ap !== bp) return ap - bp;
    return (b.createdAt || 0) - (a.createdAt || 0);
  });
}

/** One-line description of the proposed action, defensively formatted. */
export function describeAction(item: QueueItem): string {
  if (item.summary) return item.summary;
  const a = item.action;
  if (!a) return 'Pending admin action';
  const f = a.fields || {};
  const fieldStr = Object.keys(f).length
    ? ` (${Object.entries(f)
        .map(([k, v]) => `${k}=${String(v)}`)
        .join(', ')})`
    : '';
  return `${a.op || '?'} ${a.entity || '?'}${fieldStr}`;
}

/* ════════════════════ callable transport ════════════════════ */

/**
 * Fetch the durable queue. Lazy-imports firebase so the pure helpers above
 * stay unit-testable without the SDK (same discipline as localChat.ts).
 */
export async function fetchAdminActionQueue(): Promise<QueueItem[]> {
  const [{ functions }, { httpsCallable }] = await Promise.all([
    import('./firebase'),
    import('firebase/functions'),
  ]);
  const fn = httpsCallable<
    { status?: string; limit?: number },
    { items: QueueItem[] }
  >(functions, 'listAdminActionQueue');
  const res = await fn({});
  return sortForReview(res.data.items || []);
}

/**
 * Apply an owner decision to ONE queued item. The server re-checks admin
 * role, single-use, and re-derives the risk tier before executing — the
 * client verdict is never trusted.
 */
export async function decideAdminAction(
  queueId: string,
  decision: 'approve' | 'reject',
  rejectReason?: string,
): Promise<DecideResult> {
  const [{ functions }, { httpsCallable }] = await Promise.all([
    import('./firebase'),
    import('firebase/functions'),
  ]);
  const fn = httpsCallable<
    { queueId: string; decision: string; rejectReason?: string },
    DecideResult
  >(functions, 'decideAdminAction');
  const res = await fn({ queueId, decision, rejectReason });
  return res.data;
}
